import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Copy, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Section, Eyebrow, GlassCard } from "../components/ui-shared";
import { Field, TextArea, Select } from "../components/FormField";

const BUDGETS = ["Under ₹25,000", "₹25,000 – ₹1,00,000", "₹1,00,000 – ₹5,00,000", "₹5,00,000+", "Flexible / not sure yet"];
const FREQ = ["One-off campaign", "Monthly", "Quarterly", "Ongoing / always-on"];
const INDUSTRIES = ["D2C / E-commerce", "Fashion & Beauty", "Food & Beverage", "Tech / SaaS", "Health & Fitness", "Travel & Hospitality", "Finance", "Education", "Other"];

const required = ["full_name", "business_name", "email", "phone", "industry", "location", "creator_requirements", "campaign_budget", "campaign_frequency", "marketing_challenge"];

export default function BusinessApply() {
  const [form, setForm] = useState({
    full_name: "", business_name: "", email: "", phone: "", website: "", instagram: "",
    industry: "", location: "", creator_requirements: "", campaign_budget: "", campaign_frequency: "", marketing_challenge: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    required.forEach((k) => { if (!form[k].trim()) errs[k] = "Required"; });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fill in all required fields."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/applications/business", form);
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Section className="min-h-[70vh]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <GlassCard hover={false} data-testid="success-confirmation-card" className="max-w-xl mx-auto p-8 sm:p-12 text-center">
            <span className="grid place-items-center h-16 w-16 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/25 text-emerald-300">
              <CheckCircle2 size={30} />
            </span>
            <h1 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-white">Application received.</h1>
            <p className="mt-3 text-slate-400">We'll be in touch regarding Private Beta onboarding.</p>
            <div className="mt-6 flex items-center justify-center gap-2 font-mono text-sm">
              <span className="text-slate-500">Reference:</span>
              <span className="text-purple-300">{result.id}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(result.id); toast.success("Reference copied"); }}
                className="text-slate-400 hover:text-white" data-testid="copy-reference-button" aria-label="Copy reference"
              ><Copy size={14} /></button>
            </div>
            <Link to="/" className="btn-ghost mt-8">Back to Home</Link>
          </GlassCard>
        </motion.div>
      </Section>
    );
  }

  return (
    <Section>
      <div className="max-w-2xl mx-auto">
        <Eyebrow>Business Private Beta</Eyebrow>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">REQUEST BUSINESS BETA ACCESS</h1>
        <p className="mt-3 text-slate-400">Tell us about your campaign and what you're looking for. It takes about two minutes.</p>

        <form onSubmit={submit} data-testid="business-form-container" className="mt-10 glass rounded-2xl p-6 sm:p-8 space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required error={errors.full_name} />
            <Field label="Business / Brand Name" name="business_name" value={form.business_name} onChange={onChange} required error={errors.business_name} />
            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required error={errors.email} />
            <Field label="Phone / WhatsApp" name="phone" value={form.phone} onChange={onChange} required error={errors.phone} />
            <Field label="Instagram / Website" name="website" value={form.website} onChange={onChange} placeholder="Optional" />
            <Select label="Industry" name="industry" value={form.industry} onChange={onChange} options={INDUSTRIES} required error={errors.industry} />
            <Field label="Location" name="location" value={form.location} onChange={onChange} required error={errors.location} />
            <Select label="Typical campaign budget" name="campaign_budget" value={form.campaign_budget} onChange={onChange} options={BUDGETS} required error={errors.campaign_budget} />
            <Select label="Campaign frequency" name="campaign_frequency" value={form.campaign_frequency} onChange={onChange} options={FREQ} required error={errors.campaign_frequency} />
          </div>
          <TextArea label="What type of creator are you looking for?" name="creator_requirements" value={form.creator_requirements} onChange={onChange} required error={errors.creator_requirements} placeholder="Niche, audience, style, platforms…" />
          <TextArea label="What is your biggest challenge with creator marketing?" name="marketing_challenge" value={form.marketing_challenge} onChange={onChange} required error={errors.marketing_challenge} />

          <button type="submit" disabled={submitting} data-testid="business-form-submit-button" className="btn-primary w-full !mt-2 disabled:opacity-60">
            {submitting ? "Submitting…" : <>Submit Application <ArrowRight size={18} /></>}
          </button>
        </form>
      </div>
    </Section>
  );
}
